// src/pages/SalesmanOrder.js
import React, { useEffect, useState, useMemo } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  serverTimestamp,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getCachedMasterData, setCachedMasterData } from "../utils/cacheManager";
import { db, auth } from "../firebase";
import BlankLayout from "../components/BlankLayout";
import { useSearchParams, useNavigate } from "react-router-dom";

// =========================
// HELPERS
// =========================
const getTodayKey = () => {
  const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const today = map[new Date().getDay()];
  return today === "sun" ? "sat" : today;
};

// =========================
// PAGE
// =========================
function SalesmanOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [userProfile, setUserProfile] = useState(null);
  const [partyList, setPartyList] = useState([]);
  const [party, setParty] = useState(null);
  const [skuMaster, setSkuMaster] = useState({});
  const [skuList, setSkuList] = useState([]);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [avgMap, setAvgMap] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const dayKey = getTodayKey();

  // AUTH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists()) {
        alert("User profile missing");
        setLoading(false);
        return;
      }
      setUserProfile(snap.data());
    });
    return () => unsub();
  }, [navigate]);

  // LOAD PARTY LIST
  useEffect(() => {
    if (!userProfile) return;
    const loadParties = async () => {
      try {
        const metaSnap = await getDoc(doc(db, "metadata", "masterData"));
        const meta = metaSnap.exists() ? metaSnap.data() : {};
        const partyVer = meta.partyLastUpdated?.toMillis?.() || meta.partyLastUpdated?.seconds || 0;

        const pType = userProfile.partytype || "hawker";
        const cacheKey = "PARTY_LIST_SALESMAN_" + pType.toUpperCase();

        const cached = getCachedMasterData("PARTY_MASTER");
        if (cached && cached.version === partyVer && partyVer !== 0) {
          const salesmanCached = getCachedMasterData(cacheKey);
          if (salesmanCached && salesmanCached.version === partyVer) {
            setPartyList(salesmanCached.data);
            setLoading(false);
            return;
          }
        }

        const q = query(
          collection(db, "partyMaster"),
          where("clientId", "==", userProfile.clientId),
          where("partytype", "==", pType)
        );
        const snap = await getDocs(q);
        const arr = snap.docs.map(d => d.data());
        setPartyList(arr);
        setCachedMasterData(cacheKey, arr, partyVer);
        setLoading(false);
      } catch (e) {
        console.error("Error loading parties:", e);
        setLoading(false);
      }
    };
    loadParties();
  }, [userProfile]);

  // AUTO SELECT PARTY FROM URL
  useEffect(() => {
    if (!partyList.length) return;
    const pid = searchParams.get("partyId");
    if (pid) {
      const match = partyList.find(p => p.partyId === pid);
      if (match) setParty(match);
    }
  }, [partyList, searchParams]);

  // LOAD SKU MASTER
  useEffect(() => {
    if (!userProfile) return;
    const loadSkuMaster = async () => {
      try {
        const metaSnap = await getDoc(doc(db, "metadata", "masterData"));
        const meta = metaSnap.exists() ? metaSnap.data() : {};
        const skuVer = meta.skuLastUpdated?.toMillis?.() || meta.skuLastUpdated?.seconds || 0;

        const cached = getCachedMasterData("SKU_MASTER_SALESMAN");
        if (cached && cached.version === skuVer && skuVer !== 0) {
          setSkuMaster(cached.data.map);
          setItems(cached.data.list);
          setSkuList(cached.data.order);
          console.log("SalesmanOrder: Loaded from Cache");
          return;
        }

        const q = query(
  collection(db, "skuMaster"),
  where("clientId", "==", userProfile.clientId),
          orderBy("sortorder")
        );
        const snap = await getDocs(q);
        const map = {};
        const list = {};
        const order = [];
        snap.forEach(d => {
          const data = d.data();
          const sku = data.sku.trim().toUpperCase();
          map[sku] = data;
          list[sku] = "";
          order.push(sku);
        });
        setSkuMaster(map);
        setItems(list);
        setSkuList(order);
        setCachedMasterData("SKU_MASTER_SALESMAN", { map, list, order }, skuVer);
      } catch (e) {
        console.error("Error loading SKU master:", e);
      }
    };
    loadSkuMaster();
  }, [userProfile]);

  // LOAD DAY AVG
  useEffect(() => {
    const loadDayAvg = async () => {
      let text = "";
      try {
        // 1. Fetch Dynamic URL from Settings (Absolute Source of Truth)
        const settingsSnap = await getDoc(doc(db, "settings", "google_sheets"));
        const settings = settingsSnap.exists() ? settingsSnap.data() : {};
        const csvUrl = settings.dayAvgCsvUrl;

        if (!csvUrl) {
          throw new Error("Performance Data Link not configured in System Settings.");
        }

        const res = await fetch(csvUrl);
        text = await res.text();
        // Cache successfully fetched CSV
        localStorage.setItem("salesman_day_avg_csv", text);
      } catch (e) {
        console.warn("Could not fetch fresh performance data, falling back to local cache:", e.message);
        text = localStorage.getItem("salesman_day_avg_csv") || "";
      }

      if (!text) {
        console.warn("No day average performance data available offline.");
        return;
      }

      try {
        const rows = text.trim().split("\n").map(r => r.split(","));
        const header = rows[0].map(h => h.trim().toLowerCase());
        const body = rows.slice(1);
        const map = {};
        body.forEach(rowArr => {
          const row = {};
          header.forEach((h, i) => { row[h] = rowArr[i]?.trim(); });
          const salesman = row.salesman?.toUpperCase();
          const sku = row.sku?.toUpperCase();
          if (!salesman || !sku) return;
          if (!map[salesman]) map[salesman] = {};
          map[salesman][sku] = {
            mon: Number(row.mon || 0), tue: Number(row.tue || 0),
            wed: Number(row.wed || 0), thu: Number(row.thu || 0),
            fri: Number(row.fri || 0), sat: Number(row.sat || 0),
            sun: Number(row.sun || 0),
          };
        });
        setAvgMap(map);
      } catch (parseError) {
        console.error("Failed to parse performance CSV data:", parseError);
      }
    };
    loadDayAvg();
  }, []);

  // AUTO FILL DAY AVG
  useEffect(() => {
    if (!party || !avgMap || Object.keys(avgMap).length === 0) return;
    const updated = { ...items };
    skuList.forEach((sku) => {
      const avg = avgMap?.[party.partyId]?.[sku]?.[dayKey];
      if (avg && avg > 0) updated[sku] = String(avg);
    });
    setItems(updated);
  }, [party, avgMap, dayKey, skuList]);

  // TOTALS
  const totals = useMemo(() => {
    let totalQty = 0;
    let totalAmount = 0;
    Object.entries(items).forEach(([sku, qtyRaw]) => {
      const qty = Number(qtyRaw || 0);
      if (qty <= 0) return;
      const m = skuMaster[sku];
      if (!m) return;
      const basic = qty * Number(m.Basic_Eway || 0);
      const mrpTotal = qty * (Number(m.mrp) || 0);
      const gst = mrpTotal - (mrpTotal / (1 + ((Number(m.gst) || 0) / 100)));
      const cess = basic * (Number(m.cess || 0) / 100);
      const addl = qty * Number(m.addlcess || 0);
      const rebate = qty * Number(m.Rebate || 0);
      totalQty += qty;
      totalAmount += basic + gst + cess + addl + rebate;
    });
    return { totalQty, totalAmount };
  }, [items, skuMaster]);

  // ACTIONS
  const adjustQty = (sku, delta) => {
    if (!party) {
      alert("⚠️ Please select Party Name first");
      return;
    }
    if (skuMaster[sku]?.isLocked) return;

    const step = Number(skuMaster[sku]?.unitsPerOuter || 1.0);
    const current = Number(items[sku] || 0);
    // Dynamic increment based on package size
    const next = Math.max(0, current + (delta * step));
    // Clean up precision to 3 decimals
    const rounded = Math.round(next * 1000) / 1000;
    
    setItems({ ...items, [sku]: rounded === 0 ? "" : String(rounded) });
    if (navigator.vibrate) navigator.vibrate(50);
  };

  const submitOrder = async () => {
    if (!party) {
      alert("⚠️ Please select Party Name");
      return;
    }
    const minQty = Number(party.routePotential || 0);
    if (minQty > 0 && totals.totalQty < minQty) {
      alert(`⚠️ Minimum order for ${party.partyId} is ${minQty.toFixed(2)} M.\nCurrent: ${totals.totalQty.toFixed(2)} M`);
      return;
    }

    const deposit = Number(party.depositAmount || 0);
    if (deposit > 0 && totals.totalAmount > deposit) {
      alert(`⚠️ ORDER BLOCKED!\nAmount: ₹${totals.totalAmount.toFixed(0)}\nLimit: ₹${deposit.toFixed(0)}\n\nPlease reduce items or contact office.`);
      return;
    }

    setShowPreview(true);
  };

  const confirmOrder = async () => {
    if (submitting || !party || !userProfile) return;
    try {
      setSubmitting(true);
      const cleanItems = {};
      Object.entries(items).forEach(([sku, qty]) => {
        const q = Number(qty || 0);
        if (q > 0) cleanItems[sku] = q;
      });
      await addDoc(collection(db, "orders"), {
        clientId: userProfile.clientId,
        partyId: party.partyId,
        partyName: party.partyId,
        items: cleanItems,
        totalQty: totals.totalQty,
        totalAmount: totals.totalAmount,
        source: "SALESMAN_APP",
        status: "PENDING",
        payment_mode: party.payment_mode || "Cash",
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      if (navigator.vibrate) navigator.vibrate(200);
      const reset = {};
      skuList.forEach((s) => (reset[s] = ""));
      setItems(reset);
      setShowPreview(false);
    } catch (err) {
      console.error(err);
      alert("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // CONDITIONAL RENDERS
  if (loading) return <BlankLayout><p style={{ padding: 20 }}>Loading...</p></BlankLayout>;

  if (submitted) {
    return (
      <BlankLayout>
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "#198754" }}>✅ Order Submitted</h2>
          <p style={{ fontSize: 18, marginTop: 10 }}>Your order has been recorded.</p>
          <button
            onClick={() => navigate("/base")}
            style={{ marginTop: 25, padding: "14px 24px", fontSize: 18, background: "#0d6efd", color: "#fff", border: "none", borderRadius: 12 }}
          >
            Go to Dashboard
          </button>
        </div>
      </BlankLayout>
    );
  }

  if (showPreview) {
    return (
      <BlankLayout>
        <div style={{ padding: "20px", background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
          
          <div style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
            <button 
              onClick={() => setShowPreview(false)} 
              style={{ padding: "12px", background: "#fff", border: "1px solid #ddd", borderRadius: "12px", marginRight: "12px", cursor: "pointer" }}
            >
              ←
            </button>
            <h2 style={{ margin: 0, fontSize: "26px", fontWeight: "900", color: "#212529" }}>Review Order</h2>
          </div>

          <div style={{ background: "#fff", padding: "20px", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", marginBottom: "24px" }}>
            <div style={{ fontSize: "14px", fontWeight: "800", color: "#adb5bd", textTransform: "uppercase", marginBottom: "4px" }}>Customer</div>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#212529" }}>{party?.partyId}</div>
          </div>

          <div style={{ background: "#fff", borderRadius: "16px", padding: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)", marginBottom: "24px" }}>
            <div style={{ fontSize: "14px", fontWeight: "800", color: "#adb5bd", textTransform: "uppercase", marginBottom: "12px", paddingLeft: "4px" }}>Order Items</div>
            <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
              {Object.entries(items).map(([sku, qty]) => {
                if (!qty || Number(qty) <= 0) return null;
                return (
                  <div key={sku} style={{ display: "flex", justifyContent: "space-between", padding: "14px 4px", borderBottom: "1px solid #f8f9fa" }}>
                    <div style={{ fontSize: "18px", fontWeight: "700", color: "#495057", flex: 1 }}>{skuMaster[sku]?.sku}</div>
                    <div style={{ fontSize: "20px", fontWeight: "900", color: "#212529", marginLeft: "12px" }}>{qty} M</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ background: "#212529", padding: "24px", borderRadius: "20px", color: "#fff", marginBottom: "30px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
              <span style={{ fontSize: "16px", fontWeight: "600", color: "#dee2e6" }}>Total Quantity</span>
              <span style={{ fontSize: "24px", fontWeight: "900" }}>{totals.totalQty.toFixed(2)} M</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #495057", paddingTop: "12px" }}>
              <span style={{ fontSize: "16px", fontWeight: "600", color: "#dee2e6" }}>Estimated Amount</span>
              <span style={{ fontSize: "28px", fontWeight: "900", color: "#40c057" }}>₹{totals.totalAmount.toFixed(0)}</span>
            </div>
          </div>

          <button
            onClick={confirmOrder}
            disabled={submitting}
            style={{
              width: "100%",
              padding: "20px",
              background: submitting ? "#aaa" : "#2b8a3e",
              color: "#fff",
              border: "none",
              borderRadius: "16px",
              fontSize: "22px",
              fontWeight: "900",
              cursor: "pointer",
              boxShadow: "0 10px 20px rgba(43, 138, 62, 0.3)",
              marginBottom: "16px"
            }}
          >
            {submitting ? "SENDING ORDER..." : "CONFIRM & SEND ORDER →"}
          </button>

          <button
            onClick={() => setShowPreview(false)}
            style={{
              width: "100%",
              padding: "18px",
              background: "#fff1f1",
              color: "#e03131",
              border: "2px solid #ffa8a8",
              borderRadius: "16px",
              fontSize: "20px",
              fontWeight: "900",
              cursor: "pointer",
              marginTop: "12px",
              boxShadow: "0 4px 6px rgba(224, 49, 49, 0.1)"
            }}
          >
            ❌ EDIT ITEMS (GO BACK)
          </button>
        </div>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "#f8f9fa" }}>
        {/* STICKY HEADER */}
        <div style={{ padding: "16px", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", zIndex: 10 }}>
          {!isOnline && (
            <div style={{
              background: "#fee2e2",
              color: "#b91c1c",
              padding: "10px 14px",
              borderRadius: "10px",
              marginBottom: "12px",
              fontSize: "13px",
              fontWeight: "700",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "1px solid #fca5a5"
            }}>
              <span>⚠️</span>
              <span>Offline Mode: Orders will sync automatically once internet is recovered.</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={() => navigate("/base")} style={{ padding: "8px 12px", background: "#f0f0f0", border: "1px solid #ccc", borderRadius: 8 }}>← HOME</button>
            <span style={{ fontWeight: "700", color: "#666" }}>{dayKey.toUpperCase()}</span>
          </div>
          <select
            value={party?.partyId || ""}
            onChange={e => setParty(partyList.find(p => p.partyId === e.target.value))}
            style={{ width: "100%", padding: "14px", fontSize: "18px", fontWeight: "700", borderRadius: "10px", border: `2px solid ${!party ? "#ffc107" : "#ddd"}`, background: !party ? "#fff9e6" : "#fff", marginBottom: 12 }}
          >
            <option value="">-- SELECT PARTY NAME --</option>
            {partyList.map(p => <option key={p.partyId} value={p.partyId}>{p.partyId}</option>)}
          </select>
          <input
            type="text" placeholder="🔍 Search Product..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", height: 48, padding: "0 14px", fontSize: 16, borderRadius: 10, border: "1px solid #bbb" }}
          />

          {party && Number(party.depositAmount || 0) > 0 && (
            <div style={{ 
              marginTop: 10, 
              padding: "8px 12px", 
              background: totals.totalAmount > party.depositAmount ? "#fff5f5" : "#f0fff4", 
              border: `1px solid ${totals.totalAmount > party.depositAmount ? "#ffa8a8" : "#b2f2bb"}`, 
              borderRadius: 8,
              display: "flex",
              justifyContent: "space-between",
              fontSize: "14px",
              fontWeight: "700"
            }}>
              <span style={{ color: "#666" }}>Deposit Limit:</span>
              <span style={{ color: totals.totalAmount > party.depositAmount ? "#e03131" : "#2f9e44" }}>
                ₹{Number(party.depositAmount).toFixed(0)}
              </span>
            </div>
          )}
        </div>

        {/* SCROLLABLE LIST */}
        <div style={{ flex: 1, position: "relative", overflowY: "auto", padding: "16px 16px 120px 16px" }}>
          {!party && (
            <div style={{ 
              position: "absolute", 
              top: 0, left: 0, right: 0, bottom: 0, 
              background: "rgba(255,255,255,0.7)", 
              backdropFilter: "blur(6px)",
              zIndex: 50,
              display: "flex", 
              flexDirection: "column", 
              alignItems: "center", 
              paddingTop: "60px",
              textAlign: "center"
            }}>
              <div style={{ 
                fontSize: "60px", 
                marginBottom: "20px", 
                animation: "bounce 2s infinite",
                color: "#f59e0b"
              }}>
                ☝️
              </div>
              <h2 style={{ fontSize: "24px", fontWeight: "900", color: "#1f2937", margin: "0 20px" }}>
                CHOOSE YOUR NAME
              </h2>
              <p style={{ color: "#4b5563", fontSize: "16px", marginTop: "10px", fontWeight: "600" }}>
                to enable ordering list
              </p>
              
              <style>{`
                @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
                  40% {transform: translateY(-20px);}
                  60% {transform: translateY(-10px);}
                }
              `}</style>
            </div>
          )}

          {party && Number(party.routePotential || 0) > 0 && (
            <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, background: totals.totalQty >= party.routePotential ? "#dcfce7" : "#fff3cd", border: `2px solid ${totals.totalQty >= party.routePotential ? "#28a745" : "#ffc107"}`, display: "flex", justifyContent: "space-between" }}>
              <span>Target: <b>{Number(party.routePotential).toFixed(2)}</b></span>
              <span>Loaded: <b style={{ color: totals.totalQty >= party.routePotential ? "#155724" : "#856404" }}>{totals.totalQty.toFixed(2)}</b></span>
            </div>
          )}

          {skuList
            .filter((sku) => skuMaster[sku]?.sku?.toLowerCase().includes(search.toLowerCase()))
            .map((sku) => {
              const hasQty = Number(items[sku] || 0) > 0;
              const isLocked = skuMaster[sku]?.isLocked;
              return (
                <div key={sku} style={{ marginBottom: 12, padding: 14, border: hasQty ? "2px solid #198754" : "1px solid #ddd", borderRadius: 14, background: hasQty ? "#f0fff4" : "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", opacity: isLocked ? 0.8 : 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: "700" }}>{skuMaster[sku]?.sku}</div>
                    {isLocked && <span style={{ fontSize: "12px", background: "#e03131", color: "#fff", padding: "2px 8px", borderRadius: "6px", fontWeight: "900" }}>🔒 LOCKED</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button 
                      disabled={!party || isLocked} 
                      onClick={() => adjustQty(sku, -1)} 
                      style={{ 
                        flexShrink: 0,
                        width: 50, 
                        height: 56, 
                        borderRadius: 10, 
                        background: isLocked ? "#f8f9fa" : "#fff", 
                        border: isLocked ? "2px solid #eee" : "2px solid #ffc9c9", 
                        color: isLocked ? "#ccc" : "#fa5252", 
                        fontSize: 28, 
                        fontWeight: "900" 
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number" 
                      value={items[sku]} 
                      disabled={!party || isLocked} 
                      readOnly={isLocked}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d*(\.\d{0,3})?$/.test(val)) setItems({ ...items, [sku]: val });
                      }}
                      placeholder="0"
                      style={{ 
                        flex: 1, 
                        minWidth: 0,
                        height: 56, 
                        textAlign: "center", 
                        fontSize: 24, 
                        fontWeight: "900", 
                        borderRadius: 10, 
                        border: "2px solid #ddd", 
                        background: (!party || isLocked) ? "#f8f9fa" : "#fff", 
                        color: isLocked ? "#666" : "#000",
                        WebkitAppearance: "none",
                        margin: 0
                      }}
                    />
                    <button 
                      disabled={!party || isLocked} 
                      onClick={() => adjustQty(sku, 1)} 
                      style={{ 
                        flexShrink: 0,
                        width: 50, 
                        height: 56, 
                        borderRadius: 10, 
                        background: isLocked ? "#f8f9fa" : "#fff", 
                        border: isLocked ? "2px solid #eee" : "2px solid #b2f2bb", 
                        color: isLocked ? "#ccc" : "#40c057", 
                        fontSize: 28, 
                        fontWeight: "900" 
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        {/* FOOTER */}
        <div style={{ 
          position: "fixed", 
          bottom: 0, 
          left: 0,
          right: 0,
          background: "#fff", 
          borderTop: "2px solid #eee", 
          padding: "12px 16px env(safe-area-inset-bottom) 16px", 
          boxShadow: "0 -4px 12px rgba(0,0,0,0.1)",
          boxSizing: "border-box",
          zIndex: 100
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: "18px", fontWeight: "700" }}>
            <span>Qty: <span style={{ fontSize: "24px", color: "#000" }}>{totals.totalQty.toFixed(2)}</span></span>
            <span>Amt: <span style={{ fontSize: "24px", color: totals.totalAmount > (Number(party?.depositAmount) || Infinity) && Number(party?.depositAmount) > 0 ? "#e03131" : "#000" }}>₹{totals.totalAmount.toFixed(0)}</span></span>
          </div>
          <button 
            onClick={submitOrder} 
            style={{ 
              width: "100%", 
              padding: 18, 
              background: (party && Number(party.depositAmount || 0) > 0 && totals.totalAmount > party.depositAmount) ? "#fa5252" : "#198754", 
              color: "#fff", 
              border: "none", 
              borderRadius: 14, 
              fontSize: 24, 
              fontWeight: "900",
              opacity: (party && Number(party.depositAmount || 0) > 0 && totals.totalAmount > party.depositAmount) ? 0.8 : 1
            }}
          >
            { (party && Number(party.depositAmount || 0) > 0 && totals.totalAmount > party.depositAmount) ? "❌ LIMIT EXCEEDED" : "REVIEW ORDER →" }
          </button>
        </div>
      </div>
    </BlankLayout>
  );
}

export default SalesmanOrder;
